import { useState, useCallback } from 'react';
import { useGetTestItemQuery } from '@app.raytd.com/store';
import { skipToken } from '@reduxjs/toolkit/query';
import { TestItem } from 'store/src/lib/tests/entity';

interface UseTestEditorProps {
  onEditorClose?: () => void;
}

// 1. Simplify the state: We no longer need to store a copy of the test item.
interface EditorState {
  isOpen: boolean;
  testId: number | null;
}

const initialState: EditorState = {
  isOpen: false,
  testId: null,
};

export function useTestEditor({ onEditorClose }: UseTestEditorProps = {}) {
  const [editorState, setEditorState] = useState<EditorState>({
    isOpen: false,
    testId: null,
  });

  // Data from this query is now the single source of truth for the test item.
  const {
    data: fetchedTestItem,
    isLoading,
    isError,
    isUninitialized,
    refetch,
  } = useGetTestItemQuery(editorState.testId ?? skipToken);

  const openEditor = useCallback(
    (id: number) => {
      setEditorState(currentState => {
        if (id === currentState.testId && !isUninitialized) {
          refetch()
        }

        return { isOpen: true, testId: id }
      })
    },
    [refetch, isUninitialized],
  )

  const closeEditor = useCallback(() => {
    setEditorState(initialState);
    onEditorClose?.();
  }, [onEditorClose]);

  // 4. Prevent showing stale data:
  //    Return the test item only if its ID matches the currently requested ID.
  //    This prevents the UI from briefly showing the previously fetched test
  //    while a new one is loading.
  const testItem =
    fetchedTestItem?.assessmentTestId === editorState.testId
      ? fetchedTestItem
      : null;

  return {
    isOpen: editorState.isOpen,
    testItem, // Return the derived, correct test item.
    isLoading,
    isError,
    openEditor,
    closeEditor,
  };
}